/**
 * @format
 * @module lib/commercial/sticky-leaderboard
 */
import tgamConfig from "config/tgam";
import ee from "lib/eventemitter";
import isNil from "lodash/isNil";
import { isTablet, isDesktop } from "utils/detect";
import logger from "utils/logger";
import { qs, getById, getProp } from "utils/utils";
import { isAnonymous, isRegistered, isSubscribed } from "lib/identity/api";
import { gptSlotEvent } from "lib/commercial/dfp-ads/gpt-ads";
import { adServerReady } from "lib/commercial/dfp-ads/header-bidding";
import { getUrlVars } from "utils/urls";
import { pageYOffset } from "utils/window-events";
import { userHasChurn } from "lib/audience/propensity";

const debugTag = "[STICKY-LDBD]";
const debugSticky = !isNil(getUrlVars().debugSticky); // Extra debug info for sticky

// common classes
const STICKY = "c-ad--ldbd--sticky";
const HIDING = "c-ad--ldbd--sticky--hiding";

const AD_PADDING = 40; // px = top + bottom padding around an Ad element

let stickyLeaderboard = {
  ad: null, // The advertisement HTML element

  // Bound methods
  onHiddenBound: null, // When the CSS hide animation has finished, make the ad non-sticky again
  onPageScrollBound: null, // Hide the ad when the user has scrolled far enough down the page
  onRenderedBound: null, // Process the ad after it's iframe has been rendered. The contained creative still needs to load.
  onImpressionBound: null, // Fire after 50% of the ad’s pixels are visible in the browser window for a continuous 1 second.

  // Pixels values
  scrollUpTotal: 0, // Track total distance scrolled (until buffer is limit is past)
  lastYOffset: 0, // Previous pageYOffset value. Used to track scroll upwards distance.
  stickYOffset: 0, // Height of site header (start being sticky)
  unstickYOffset: 0, // After reaching this y position, stop being sticky.

  hasStuck: false, // True after the Ad has been sticky, which should only be once.

  state: "empty", // Advert's lifecycle state, is either:
  // 1. empty = initial state before any events have occured.
  // 2. rendered = after the GPT Ad's `slotRenderEnded` event has fired once.
  // 3. impression = after the GPT Ad's `impressionViewable` event has fired once.
  // 4. shutdown = a single Ad was successfully stuck once, or Ad loaded is too big (abort).
  // 5. off = shutdown has completed.

  /**
   * @method init
   * @description Initialize the sticky leaderboard ad by attaching a mutation observer to it
   */
  init() {
    const header = qs(".c-site-header");
    this.ad = getById("c-ad--ldbd-gpt-1"); // wraps the leaderboard advertisement
    logger.info(`${debugTag} init:`, { element: this.ad });

    if (!this.ad || !header) {
      return;
    }

    this.lastYOffset = pageYOffset(); // current scroll position
    this.stickYOffset = header.getBoundingClientRect().height;

    // Bind functions to ensure that "this" will reference the "stickyLeaderboard" object
    this.onRenderedBound = this.onRendered.bind(this);
    this.onImpressionBound = this.onImpression.bind(this);
    this.onPageScrollBound = this.onPageScroll.bind(this);
    this.onHiddenBound = this.onHidden.bind(this);

    const slodId = "ldbd-gpt-1";

    gptSlotEvent(slodId, "slotRenderEnded", this.onRenderedBound);
    gptSlotEvent(slodId, "impressionViewable", this.onImpressionBound);

    this.onPageScroll(pageYOffset()); // trigger scroll once
    ee.on("windowScroll.throttled", this.onPageScrollBound);
    this.ad.addEventListener("animationend", this.onHiddenBound);
  },

  /**
   * @method onImpression
   * @description Fired after 50% of the (loaded) ad’s pixels are visible in the browser window for
   * a continuous 1 second.
   * @param {Object} event - GPT onload event
   */
  onImpression(event) {
    if (this.state !== "rendered") {
      // This `impressionViewable` GPT event can't be unbound, so this check is neccessary. It will
      // be triggered for refreshed Ldbd Ad's too. So we can early exit here for all other Ad events.
      // Only the initial Ad (event) needs to stick once.
      return;
    }
    this.state = "impression";

    logger.info(`${debugTag} impression:`, { slotId: event.slot.getSlotElementId() });

    setTimeout(() => {
      // Double-check state value, since it could be different, after waiting (2 seconds of user input).
      if (this.state === "impression") {
        this.state = "shutdown";
        this.onPageScroll(pageYOffset()); // trigger scroll
      }
      // This is 2 seconds, since the impression event takes 1 second to fire.
      // So, this method really executes after 3 seconds total.
    }, 2000);
  },

  /**
   * @method onRendered
   * @description Process the ad after it's iframe has been rendered. This event will be
   * fired again, after the ad has been refreshed. The creative content still needs to load.
   * @param {Object} event - GPT onload event
   */
  onRendered(event) {
    if (this.state !== "empty") {
      // This `slotRenderEnded` GPT event can't be unbound, so this check is neccessary. It will
      // be triggered for refreshed Ldbd Ad's too. So we can early exit here for all other Ad events.
      // Since all refreshed Ads should be the same dimensions as the first Ad.
      return;
    }
    this.state = "rendered";

    // ARC-4820: Remove sticky leaderboard on tablet bp's when 970x250 ads are returned
    const height = event.size[1] || 0;
    const adShouldStick = isDesktop() && height <= 90;
    if (!adShouldStick) {
      this.state = "shutdown";
    }

    // maintain an Advert placeholder in the page layout
    this.ad.parentElement.style.height = height + AD_PADDING + "px";

    // value of window.pageYOffset, on article page when `c-ad--halfpager-gpt-1` is at top of viewport.
    this.unstickYOffset = 1400; // this will allow users that quickly scroll down, to not see the sticky ad.

    logger.info(`${debugTag} rendered:`, {
      size: event.size,
      hb_size: event.slot.getTargeting("hb_size"),
      shouldStick: adShouldStick,
      stickYOffset: this.stickYOffset,
      unstickYOffset: this.unstickYOffset,
      slotId: event.slot.getSlotElementId()
    });

    this.onPageScroll(pageYOffset()); // trigger scroll
  },

  /**
   * @method onPageScroll
   * @description Hide the ad when the user has scrolled far enough down the page
   * @param {Number} scrollPos - equal to current window.pageYOffset
   */
  onPageScroll(scrollPos) {
    // Don't try to re-position the Ldbd Ad, until the initial Ad (iframe) has rendered.
    if (this.state === "empty" || this.state === "off") {
      debugSticky && logger.info(`${debugTag} empty`);
      return;
    }

    // Check for shutdown phase.
    if (this.state === "shutdown") {
      debugSticky && logger.info(`${debugTag} shutdown`);
      this.state = "off";

      if (scrollPos < this.stickYOffset) {
        // The Ad is already unstuck
        this.onHidden(); // hide, then make idle
        this.unbind();
      } else if (scrollPos >= this.stickYOffset) {
        // The Ad needs to become unstuck
        this.unsticky(); // unsticky, hide, then make idle
        this.unbind();
      }
      return;
    }

    const scrolled = Math.abs(this.lastYOffset - scrollPos);
    if (scrolled <= 0) {
      return;
    }

    const scrollingDown = scrollPos >= this.lastYOffset;
    if (scrollingDown) {
      this.scrollUpTotal = 0;
    } else {
      this.scrollUpTotal += scrolled;
    }

    // Hide the sticky Ad after user has scrolled up past this pixel limit
    const scrollingUpwards = this.scrollUpTotal >= 100;

    debugSticky &&
      logger.info(`${debugTag} scroll:`, {
        state: this.state,
        pos: scrollPos,
        last: this.lastYOffset,
        scroll: scrolled,
        total: this.scrollUpTotal,
        down: scrollingDown,
        up: scrollingUpwards
      });

    this.lastYOffset = scrollPos;

    if (scrollPos < this.stickYOffset) {
      this.idle();
    } else if (scrollingDown && scrollPos >= this.stickYOffset && scrollPos < this.unstickYOffset) {
      this.sticky();
    } else if ((scrollingDown && scrollPos >= this.unstickYOffset) || scrollingUpwards) {
      this.state = "shutdown";
      this.onPageScroll(pageYOffset()); // trigger scroll
    }
  },

  /**
   * @method sticky
   * @description Makes the ad sticky.
   */
  sticky() {
    // The Ad should only be sticky once. This flag prevents that.
    if (!this.hasStuck) {
      this.hasStuck = true;
      debugSticky && logger.info(`${debugTag} sticky`);
      this.ad.classList.add(STICKY);
    }
  },

  /**
   * @method unsticky
   * @description Hide the ad. Triggers a CSS `animationend` animation.
   */
  unsticky() {
    // No need to perform the unsticky hide animation, if the Ad never was stuck.
    if (this.hasStuck) {
      debugSticky && logger.info(`${debugTag} unsticky`);
      this.ad.classList.add(HIDING);
    }
  },

  /**
   * @method onHidden
   * @description When the CSS hide animation has finished, make the ad non-sticky again.
   */
  onHidden() {
    debugSticky && logger.info(`${debugTag} hidden`);
    this.ad.removeEventListener("animationend", this.onHiddenBound);
    this.idle();
  },

  /**
   * @method idle
   * @description Default location of the ad (not sticky)
   */
  idle() {
    if (this.ad.classList.contains(HIDING) || this.ad.classList.contains(STICKY)) {
      this.ad.classList.remove(HIDING);
      this.ad.classList.remove(STICKY);
      debugSticky && logger.info(`${debugTag} idle`);
    }
  },

  /**
   * @method unbind
   * @description Unbind all events that can be unbound.
   */
  unbind() {
    debugSticky && logger.info(`${debugTag} unbind`);
    ee.off("windowScroll.throttled", this.onPageScrollBound);
    stickyLeaderboard = null; // garbage collect me plz?
  }
};

/**
 * @method isValidPath
 * @description determines if the current section or article should stick the leaderboard ad.
 * @param {String} ss - sticky leaderboard sections
 * @returns {Boolean}
 */
function isValidPath(ss) {
  const pt = tgamConfig.meta.pagetype;
  const url = tgamConfig.meta.urlRelative;
  if (!url || (pt !== "story" && pt !== "section")) {
    return false;
  }
  if (ss === "*") {
    return true;
  }
  const valid = ss.split(",").filter(path => url.includes(path)).length > 0;
  return valid;
}

/**
 * @method init
 * @description Initialize the sticky leaderboard ad if the conditions have been met
 */
export default function init() {
  const userToChurn = userHasChurn(tgamConfig);
  const notMobile = isTablet() || isDesktop();

  const sectionsOtherUsers = getProp(tgamConfig, "adops.stickyLdbdSectionsOtherUsers", "");
  const sectionsSubscriber = getProp(tgamConfig, "adops.stickyLdbdSectionsSubscriber", "");
  const isSticky =
    (isValidPath(sectionsOtherUsers) && (isAnonymous() || (isRegistered() && !userToChurn))) ||
    (isValidPath(sectionsSubscriber) && isSubscribed());

  debugSticky && logger.info(`${debugTag} default:`, { notMobile, isSticky });
  if (notMobile && isSticky) {
    adServerReady.then(() => stickyLeaderboard.init());
  }
}
